import React, { useEffect, useState } from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  JustifyContent,
  Overflow,
  Text,
} from '@gousto-internal/citrus-react'

import { useNumRecipesOverBasketLimit } from 'routes/Menu/domains/basket'

import { BoxProgressMessage } from '../../BoxProgressMessage'
import { TooManyRecipesWarning } from '../../TooManyRecipesWarning'

type RecipesInBasketProgressContentProps = {
  selectedRecipesCount: number
  percentage: number
}

const getProgressColor = (percentage: number) => {
  if (percentage >= 100) {
    return Color.Success_600
  }

  if (percentage >= 80) {
    return Color.Warning_500
  }

  return Color.Error_800
}

const RecipesInBasketProgressContent = ({
  selectedRecipesCount,
  percentage,
}: RecipesInBasketProgressContentProps) => {
  const numRecipesOverLimit = useNumRecipesOverBasketLimit()
  const isBasketFull = percentage === 100
  const progressColor = getProgressColor(percentage)
  const [fullAnimationKey, setFullAnimationKey] = useState(0)
  const [isFullStateVisible, setIsFullStateVisible] = useState(false)

  useEffect(() => {
    if (!isBasketFull) {
      setIsFullStateVisible(false)

      return undefined
    }

    setIsFullStateVisible(false)
    setFullAnimationKey((currentKey) => currentKey + 1)

    const animationTimeout = window.setTimeout(() => {
      setIsFullStateVisible(true)
    }, 60)

    return () => {
      window.clearTimeout(animationTimeout)
    }
  }, [isBasketFull])

  if (numRecipesOverLimit) {
    return <TooManyRecipesWarning numRecipesOverLimit={numRecipesOverLimit} />
  }

  return (
    <Box height="3.5rem" display={Display.Flex} alignItems={AlignItems.Center} width="100%">
      {isBasketFull ? (
        <Box
          key={fullAnimationKey}
          display={Display.Flex}
          alignItems={AlignItems.Center}
          gap={3}
          width="100%"
          color={Color.ColdGrey_700}
          style={{
            opacity: isFullStateVisible ? 1 : 0.6,
            transform: isFullStateVisible ? 'translateY(0)' : 'translateY(4px)',
            transition: 'opacity 220ms ease-out, transform 220ms ease-out',
          }}
        >
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            bg={Color.Success_100}
            minWidth="1.75rem"
            width="1.75rem"
            height="1.75rem"
            flexShrink={0}
            style={{
              borderRadius: '999px',
              opacity: isFullStateVisible ? 1 : 0,
              transform: isFullStateVisible ? 'scale(1)' : 'scale(0.8)',
              transition: 'opacity 220ms ease-out, transform 220ms ease-out',
            }}
          >
            <Icon data-testid="tick" name="tick" size={3} color={Color.Success_600} />
          </Box>
          <Box paddingRight={[6, 0]} style={{ fontSize: '12px', marginBottom: '-12px' }}>
            <BoxProgressMessage numRecipes={selectedRecipesCount} />
          </Box>
        </Box>
      ) : (
        <Box width="100%" display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
          <Box display={Display.Flex} alignItems={AlignItems.Center} gap={4}>
            <Text color={Color.ColdGrey_800} fontWeight={FontWeight.Bold} size={4}>
              {percentage}%
            </Text>
            <Box paddingRight={[6, 0]} style={{ fontSize: '12px', marginBottom: '-12px' }}>
              <BoxProgressMessage numRecipes={selectedRecipesCount} />
            </Box>
          </Box>
          <Box
            bg={Color.NeutralGrey_100}
            borderRadius={1.5}
            height="0.5rem"
            overflowX={Overflow.Hidden}
            overflowY={Overflow.Hidden}
            width="100%"
          >
            <Box
              bg={progressColor}
              borderRadius={1.5}
              height="100%"
              width={`${percentage}%`}
              style={{ transition: 'width 500ms ease, background-color 500ms ease' }}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export { RecipesInBasketProgressContent }
